import React from 'react';
import { JobCard, JobListing } from './JobCard';

interface CacheInfo {
  lastFetchAt: string | null;
  daysUntilNextFetch: number;
  message: string;
}

interface JobListingsSectionProps {
  jobs: JobListing[];
  onJobClick?: (job: JobListing) => void;
  cacheInfo?: CacheInfo | null;
  isCached?: boolean;
}

export const JobListingsSection: React.FC<JobListingsSectionProps> = ({ 
  jobs, 
  onJobClick, 
  cacheInfo, 
  isCached 
}) => {
  // Limit to maximum 3 jobs
  const displayedJobs = jobs.slice(0, 3);

  // Calculate cache message
  const getCacheMessage = () => {
    if (!isCached || !cacheInfo || cacheInfo.daysUntilNextFetch === undefined || 
        cacheInfo.daysUntilNextFetch === null || isNaN(cacheInfo.daysUntilNextFetch)) {
      return null;
    }
    
    const days = Number(cacheInfo.daysUntilNextFetch);
    if (days < 1) {
      return 'Matches update today';
    }
    const daysCeil = Math.ceil(days);
    return `Matches update in ${daysCeil} day${daysCeil !== 1 ? 's' : ''}`;
  };

  const cacheMessage = getCacheMessage();

  return (
    <div className="card-premium p-4 md:p-8 bg-gradient-to-br from-white to-slate-50 border-slate-200">
      <div className="flex items-start justify-between mb-4 md:mb-8 gap-4">
        <div className="flex-1">
          <h2 className="heading-2 mb-1">Your Personalised Matches</h2>
          <p className="text-body text-xs md:text-base">Jobs curated specifically for your profile</p>
        </div>
        
        {/* Cache Info Badge */}
        {cacheMessage && (
          <div className="flex-shrink-0 mt-0.5">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/60 shadow-sm">
              <svg 
                className="w-3.5 h-3.5 text-blue-600" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
              <span className="text-xs font-medium text-blue-900 whitespace-nowrap">
                {cacheMessage}
              </span>
            </div>
          </div>
        )}
      </div>

      {displayedJobs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
          {displayedJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onClick={() => onJobClick?.(job)}
            />
          ))}
        </div>
      ) : (
        <div className="glass-panel p-6 md:p-12 flex flex-col items-center justify-center text-center min-h-[200px] md:min-h-[300px]">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-sm">
            <svg
              className="w-6 h-6 md:w-8 md:h-8 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2">No matches found yet</h3>
          <p className="text-slate-500 max-w-md text-xs md:text-base">
            We're still looking for the perfect roles for you. Try adjusting your preferences or check back later.
          </p>
        </div>
      )}
    </div>
  );
};
